import React, { useEffect, useMemo } from "react";
import { Row, Col, Button, Spinner } from "reactstrap";
import CommonForm from "Components/Common/CommonForm";
import SectionCard from "Components/Common/SectionCard";
import { getAdverseEventFields } from "../helper/adverseeventconstant";
import styles from "../css/adverseEvent.module.scss";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import AdverseEventStepper from "./AdverseEventStepper";

interface AdverseEventEventDetailsFormProps {
  control: any;
  register: any;
  setValue: any;
  watch: any;
  getValues: any;
  errors: any;
  clearErrors: any;
  studyOptions: any[];
  subjectsOptions: any[];
  periodOptions: any[];
  isSubjectLoading: boolean;
  onSubmit: (e: any) => void;
  isPending: boolean;
  onCancel: () => void;
  isSaved: boolean;
  onNext: () => void;
  isEditMode?: boolean;
}

const AdverseEventEventDetailsForm: React.FC<AdverseEventEventDetailsFormProps> = ({
  control,
  setValue,
  watch,
  getValues,
  errors,
  clearErrors,
  studyOptions,
  subjectsOptions,
  periodOptions,
  isSubjectLoading,
  onSubmit,
  isPending,
  onCancel,
  isSaved,
  onNext,
  isEditMode = false,
}) => {
  // Watch fields
  const watchedStudyId = watch("studycode");
  const watchedAssociatedSymptoms = watch("associatedsymptoms");
  const watchedTemperatureUnit = watch("temperatureunit");
  const watchedTemperatureLocation = watch("temperaturelocation");
  const watchedBloodPressure = watch("bloodpressure");
  const watchedPulse = watch("pulse");
  const watchedRespiratoryRate = watch("respiratoryrate");
  const watchedTemperature = watch("temperature");

  useEffect(() => {
    if (!watchedTemperatureUnit) {
      setValue("temperaturelocation", "");
      setValue("temperature", "");
      clearErrors?.("temperaturelocation");
      clearErrors?.("temperature");
    } else if (!watchedTemperatureLocation) {
      setValue("temperature", "");
      clearErrors?.("temperature");
    }
  }, [watchedTemperatureUnit, watchedTemperatureLocation, setValue, clearErrors]);

  const watchedSubjectNo = watch("subjectno");
  const selectedSubject = useMemo(() => {
    return subjectsOptions.find((s: any) => s.value === watchedSubjectNo);
  }, [watchedSubjectNo, subjectsOptions]);

  const enrollmentDate = selectedSubject?.createddate;

  const watchedOnsetOfEvent = watch("onsetofevent");
  const watchedIsFollowupRequired = watch("isfollowuprequired");

  const isAnyVitalsFieldFilled = !!(
    watchedBloodPressure ||
    watchedPulse ||
    watchedRespiratoryRate ||
    watchedTemperature ||
    watchedTemperatureUnit ||
    watchedTemperatureLocation ||
    watchedAssociatedSymptoms
  );

  const fieldState = {
    studyOptions,
    subjectsOptions,
    periodOptions,
    watchedisadverse: "no",
    isEditMode: isEditMode,
    selectedStudyId: watchedStudyId,
    isSubjectLoading,
    getValues,
    watchedTemperatureUnit,
    watchedTemperatureLocation,
    enrollmentDate,
    watchedOnsetOfEvent,
    watchedIsFollowupRequired,
    isAnyVitalsFieldFilled,
  };

  const studyFields = getAdverseEventFields("study", fieldState);
  const eventFields = getAdverseEventFields("event", fieldState);
  const vitalsFields = getAdverseEventFields("vitals", fieldState);
  const examFields = getAdverseEventFields("examination", fieldState);

  return (
    <div className={formStyles.formShell}>
      {!isEditMode && (
        <>
          {/* Page Header */}
          <div className="mb-4">
            <h4 className="mb-0 fw-700">Log New Adverse Event</h4>
          </div>

          <AdverseEventStepper activeTab="event_details" />
        </>
      )}

      {/* Form Content (Full Width) */}
      <form className={formStyles.formBody} onSubmit={onSubmit}>
        {/* Section 1: Study & Subject Information */}
        <SectionCard
          index={1}
          icon="ri-hospital-line"
          title="General & Timing Details"
          subtitle="Capture project number, participant details, onset and recording dates."
        >
          <CommonForm
            fields={studyFields.filter((f) => f.name !== "eventterm")}
            control={control}
            errors={errors}
            columns={3}
            setValue={setValue}
          />
          <div className="mt-3">
            <CommonForm
              fields={studyFields.filter((f) => f.name === "eventterm")}
              control={control}
              errors={errors}
              columns={1}
              setValue={setValue}
            />
          </div>
        </SectionCard>

        {/* Section 2: Severity, Occurrence & Report Method */}
        <SectionCard
          index={2}
          icon="ri-alert-line"
          title="Event Assessment & Occurrence"
          subtitle="Record description, severity grade, report method, occurrence, and last dose received."
        >
          <CommonForm
            fields={eventFields}
            control={control}
            errors={errors}
            columns={3}
            setValue={setValue}
          />
        </SectionCard>

        {/* Section 3: Clinical Examination & Vital Signs */}
        <SectionCard
          index={3}
          icon="ri-stethoscope-line"
          title="Clinical Examination & Vital Signs"
          subtitle="Record exam timing, blood pressure, pulse, respiratory rate, and temperature."
        >
          <CommonForm
            fields={vitalsFields.filter((f) => ["medicalexamstart", "medicalexamend"].includes(f.name))}
            control={control}
            errors={errors}
            columns={2}
            setValue={setValue}
          />
          <div className="mt-3">
            <CommonForm
              fields={vitalsFields.filter((f) => ["bloodpressure", "pulse", "respiratoryrate"].includes(f.name))}
              control={control}
              errors={errors}
              columns={3}
              setValue={setValue}
            />
          </div>
          <div className="mt-3">
            <CommonForm
              fields={vitalsFields.filter((f) => ["temperatureunit", "temperaturelocation", "temperature"].includes(f.name))}
              control={control}
              errors={errors}
              columns={3}
              setValue={setValue}
            />
          </div>
          <Row className="mt-2">
            <Col md={12}>
              <CommonForm
                fields={vitalsFields.filter((f) => f.name === "associatedsymptoms")}
                control={control}
                errors={errors}
                columns={1}
                setValue={setValue}
              />
            </Col>
          </Row>
          {watchedAssociatedSymptoms === "yes" && (
            <Row className="mt-2">
              <Col md={12}>
                <CommonForm
                  fields={vitalsFields.filter((f) => f.name === "symptomsremark")}
                  control={control}
                  errors={errors}
                  columns={1}
                  setValue={setValue}
                />
              </Col>
            </Row>
          )}
        </SectionCard>

        {/* Section 4: General Examination */}
        <SectionCard
          index={4}
          icon="ri-heart-pulse-line"
          title="General Examination"
          subtitle="Record general and systemic examinations, provisional diagnosis, and actions taken."
        >
          <CommonForm
            fields={examFields.filter((f) => f.name !== "examremark")}
            control={control}
            errors={errors}
            columns={2}
            setValue={setValue}
          />
          <div className="mt-3">
            <CommonForm
              fields={examFields.filter((f) => f.name === "examremark")}
              control={control}
              errors={errors}
              columns={1}
              setValue={setValue}
            />
          </div>
        </SectionCard>

        {/* Sticky Footer */}
        <div className={formStyles.stickyFooter}>
          <div className={formStyles.footerActions}>
            <div className="d-flex gap-2 ms-auto">
              {!isSaved && (
                <Button
                  type="submit"
                  color="primary"
                  outline
                  disabled={isPending}
                >
                  {isPending ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEditMode ? "Updating..." : "Saving..."}
                    </>
                  ) : (
                    <>{isEditMode ? "Update" : "Save"}</>
                  )}
                </Button>
              )}
              <Button
                type="button"
                color="primary"
                outline
                onClick={onNext}
                disabled={!isSaved && !isEditMode}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdverseEventEventDetailsForm;
